<template>
	<div class="thread-message-list" v-if="isMounted">
		<div class="thread-header shadow-left">
			<div
				v-if="!pinView"
				class="thread-label"
				v-t="'chat_reply_chain_title'"
			/>
			<div v-if="pinView" class="thread-label">
				<div class="pinned-label" v-text="$t('chat_pin_message_title')" />
				<div class="expiration">
					<ClockIcon class="clock-icon" type="solid" />
					{{
						$t('chat_pin_message_expiration_with_value', {
							ExpirationDate: expiresOn,
						})
					}}
				</div>
			</div>
			<Button
				v-if="!pinView && canConvertToThread && messages.length > 1"
				class="convert-thread-btn"
				:icon="ReelIcon"
				:iconProps="{ type: 'solid' }"
				@click="showConvertModal = true"
			>
				{{ $t('chat_messageaction_converttothread') }}
			</Button>
		</div>
		<ConvertToThreadModal
			v-if="showConvertModal"
			:bubbleId="bubbleId"
			:sourceMessageId="replyChainId"
			:defaultTitle="defaultThreadTitle"
			@success="onConvertSuccess"
			@cancel="showConvertModal = false"
		/>

		<LoadingBar v-show="isLoading" />
		<CloseReplyChainView @close="close" />
		<transition name="thread-view">
			<MessageList
				:messages="messages"
				:isLoadingMore="isLoadingMore"
				:readReceiptsByMessageId="{}"
				:hideReplyAction="true"
				:hideThreadLine="true"
				:clickToClose="true"
				:isListVisible="true"
				ref="list"
				idPrefix="thread_"
				@load-backward="handleLoadBackward"
				@reply="$emit('reply', $event)"
				@close="close"
				@image="$emit('image', $event)"
				@video="$emit('video', $event)"
				@jump-to-latest="scrollToBottom"
			/>
		</transition>

		<div class="status-bar-wrapper">
			<div
				class="status-bar"
				:class="{ expanded: showTypingIndicator }"
				v-text="typingText"
			/>
		</div>
	</div>
</template>

<script>
import {
	getChildMessagesInThread,
	getStartCursorForBubble,
	getMessage,
	setMessages,
	getChat,
} from '../../store'
import MessageList from './MessageList'
import CloseReplyChainView from './CloseReplyChainView'
import LoadingBar from '../LoadingBar'
import { Button } from '@/components/common'
import { fetchHistoryWithoutStore } from '@/api/chat'
import { chatCan, CONVERT_TO_THREAD } from '@/helpers/permissions'
import { addEscapeListener, removeEscapeListener } from '@/helpers/escape'
import { generateThreadTitle } from '@/helpers/utils'
import ConvertToThreadModal from './ConvertToThreadModal.vue'
import { getTypingIndicatorText } from '@/helperMethods'
import { ClockIcon, ReelIcon } from '@/icons'
import moment from 'moment'
import { MESSAGE_PAGE_SIZE } from '@/helpers/constants'

export default {
	name: 'ReplyChainView',
	props: ['replyChainId', 'bubbleId', 'typingUsers', 'pinView'],
	components: {
		MessageList,
		LoadingBar,
		CloseReplyChainView,
		ClockIcon,
		Button,
		ConvertToThreadModal,
	},
	data() {
		return {
			isLoading: false,
			isLoadingMore: false,
			isMounted: false,
			messages: [],
			typingText: null,
			showTypingIndicator: false,
			historyComplete: false,
			showConvertModal: false,
			ReelIcon,
		}
	},
	emits: ['reply', 'image', 'video', 'close'],
	mounted() {
		this.fetchHistoryIfNeeded()
		this.$nextTick(() => {
			this.isMounted = true
		})
		this.escapeId = addEscapeListener(() => this.close())

		if (!this.messages.length) {
			this.messages = this.getThreadMessages()
		}

		this.onMessageChange = () => {
			this.messages = this.getThreadMessages()
		}

		this.$onEventBus('message-change', this.onMessageChange)
	},
	beforeUnmount() {
		removeEscapeListener(this.escapeId)
	},
	methods: {
		getThreadMessages() {
			const parent = getMessage(this.threadId)
			const messages = getChildMessagesInThread(this.threadId)
			if (!parent) return messages
			if (!messages[0]) return [parent]
			if (
				!parent.firstchildmessage_id ||
				messages[0].id === parent.firstchildmessage_id
			)
				return [].concat(parent).concat(messages)
			return messages
		},

		handleLoadBackward() {
			if (this.historyComplete) return
			this.fetchHistoryIfNeeded(this.messages[0].id)
		},
		onConvertSuccess(thread) {
			this.showConvertModal = false
			this.close()
			window.EventBus.$emit('open-thread', thread)
		},
		close() {
			this.$emit('close')
		},
		fetchHistoryIfNeeded(latest) {
			const parent = getMessage(this.threadId)
			if (!parent?.firstchildmessage_id) {
				// Special case where parent is not yet part of a thread
				// and user is creating first reply message
				return false
			}
			const startCursor = getStartCursorForBubble(this.bubbleId)
			if (parent.firstchildmessage_id >= startCursor) {
				// Special case where we've already fetched all the way
				// back to the first child, so we have the whole thread
				return false
			}
			const firstChild = getMessage(parent.firstchildmessage_id)
			if (firstChild?.id === parent.lastchildmessage_id) {
				// Special case where there's only one reply. If we have
				// that reply, then we have the whole thread.
				return false
			}
			this.fetchThreadHistory(latest)
		},
		async fetchThreadHistory(latest) {
			try {
				this.isLoading = true
				this.isLoadingMore = !!latest
				const result = await fetchHistoryWithoutStore({
					bubble_id: this.bubbleId,
					thread_id: this.threadId,
					latest,
				})
				setMessages(result.messages)
				setMessages(result.parentmessages)
				if (result.messages.length < MESSAGE_PAGE_SIZE) {
					this.historyComplete = true
				}
			} catch (e) {
				console.error(e)
			} finally {
				this.isLoading = false
				this.isLoadingMore = false
			}
		},
		scrollToBottom() {
			this.$refs.list.$el.scrollTop = 0
		},
	},
	watch: {
		typingUsers(typingUsers) {
			const userIds = Object.values(typingUsers)
				.filter((t) => t.thread_id === this.threadId)
				.map((t) => t.user_id)
			if (userIds.length) {
				this.showTypingIndicator = true
				this.typingText = getTypingIndicatorText(userIds)
			} else {
				this.showTypingIndicator = false
				setTimeout(() => {
					// delay for better animation
					this.typingText = null
				}, 300)
			}
		},
	},
	computed: {
		// Alias for backward compatibility with internal code and API
		// The API still uses thread_id for reply chain pagination
		threadId() {
			return this.replyChainId
		},
		canConvertToThread() {
			const chat = getChat(this.bubbleId)
			return chatCan(chat, CONVERT_TO_THREAD)
		},
		defaultThreadTitle() {
			const sourceMessage = getMessage(this.replyChainId)
			return generateThreadTitle(sourceMessage?.message)
		},
		expiresOn() {
			const expires = getChat(this.bubbleId).pinned_message_expires_at
			const local = moment.utc(expires).local()
			return local.format('MMM Do YYYY, h:mm a')
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
$header-height: 60px;

.thread-message-list {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	flex-direction: column;
	background: var(--neutral-0);
	.thread-header {
		flex-shrink: 0;
		height: $header-height;
		display: flex;
		align-items: center;
		justify-content: center;
		background: var(--neutral-0);
		border-bottom: solid 1px var(--neutral-40040);
		.thread-label {
			width: 100%;
			text-align: center;
			font-size: 20px;
			font-family: Avenir-Heavy;
		}
		.convert-thread-btn {
			position: absolute;
			right: 16px;
			box-shadow: none;
			:deep(.pronto-btn-icon) {
				transform: rotate(30deg);
			}
		}
		.pinned-label {
			font-size: 18px;
		}
		.expiration {
			margin-top: 3px;
			font-family: Avenir-Medium;
			font-size: 12px;
			color: var(--neutral-500);
			display: flex;
			align-items: center;
			justify-content: center;
			.clock-icon {
				margin-right: 4px;
				font-size: 10px;
			}
		}
		.thread-close {
			cursor: pointer;
			position: absolute;
			right: 30px;
			&:focus {
				outline: none;
			}
		}
	}
}

:deep(.date-divider) {
	.line {
		border-top-color: #dedede;
	}
}

:deep(.message-wrapper) {
	.message-img {
		border-color: transparent;
	}
}

.status-bar-wrapper {
	position: relative;

	.status-bar {
		left: 0;
		bottom: 0;
		position: absolute;
		background-color: #ffffff;
		object-fit: contain;
		font-family: Avenir-Roman;
		font-size: 12px;
		text-align: left;
		color: #8a8d93;
		-webkit-transition: all 0.3s ease-in-out;
		transition: all 0.3s ease-in-out;
		max-height: 0;
		overflow: hidden;
		width: 100%;
		padding-left: 30px;
		@include max-z;

		&.expanded {
			max-height: 25px;
			padding-bottom: 5px;
			padding-top: 4px;
		}
	}
}

// ANIMATION
@keyframes fade-in {
	0% {
		opacity: 0.2;
	}
	100% {
		opacity: 1;
	}
}
.thread-view-enter-active {
	animation: fade-in 0.5s ease-in-out;
}
.thread-view-leave-active {
	animation: fade-in 0.5s linear reverse;
}
</style>

<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.thread-message-list {
		background: var(--neutral-300);
		.thread-header {
			background: var(--neutral-100);
		}
		.message-wrapper {
			&:not(:hover) {
				.message-img {
					border-color: var(--neutral-300);
				}
			}
		}
		.message-outer-container {
			.date-divider {
				.line {
					border-color: var(--neutral-40040) !important;
				}
			}
		}
		.status-bar {
			background: var(--neutral-300) !important;
		}
	}
}
</style>
